// EditorHistoryModal — the query editor's "local history": a timeline of version
// snapshots for this tab (left), with a diff-vs-current / full-text view of the
// selected version (right) and a one-click Restore.
//
// Props:
//   open        bool
//   tabKey      string  — the query tab's stable key (inner id)
//   currentSql  string  — the editor's current content (for the diff)
//   onRestore(sql)      — load a version back into the editor
//   onClose()
import { createSignal, createEffect, createResource, createMemo, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { listEditorVersions } from '../lib/editorhistory.js';
import { lineDiff, diffStats } from '../lib/linediff.js';

const fmtTime = (unixSec) => {
  if (!unixSec) return '';
  try { return new Date(unixSec * 1000).toLocaleString(); } catch { return String(unixSec); }
};
const relTime = (unixSec) => {
  const s = Math.max(0, Math.floor(Date.now() / 1000) - unixSec);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const firstLine = (sql) => {
  const l = String(sql || '').split('\n').find((x) => x.trim()) || '';
  return l.length > 60 ? l.slice(0, 60) + '…' : l;
};

export default function EditorHistoryModal(props) {
  const [versions, { refetch }] = createResource(
    () => (props.open && props.tabKey ? props.tabKey : false),
    (k) => listEditorVersions(k));
  const [selId, setSelId] = createSignal(null);
  const [mode, setMode] = createSignal('diff'); // 'diff' | 'full'

  // Select the newest version when the list (re)loads.
  createEffect(() => {
    const list = versions();
    if (Array.isArray(list) && list.length && (selId() == null || !list.some((v) => v.id === selId()))) {
      setSelId(list[0].id);
    }
  });
  createEffect(() => { if (props.open) refetch(); });

  const selected = createMemo(() => (versions() || []).find((v) => v.id === selId()) || null);
  const diff = createMemo(() => (selected() ? lineDiff(selected().sqlText, props.currentSql || '') : []));
  const stats = createMemo(() => diffStats(diff()));

  return (
    <Show when={props.open}>
      <Modal open={true} title="Editor history" onClose={props.onClose} wide cardClass="edhist-modal">
        <div class="edhist">
          <div class="edhist-list">
            <Show when={(versions() || []).length > 0}
                  fallback={<div class="muted" style="padding:14px;">No saved versions yet. Versions are captured as you edit.</div>}>
              <For each={versions()}>
                {(v) => (
                  <div class="edhist-item" classList={{ active: v.id === selId() }} onClick={() => setSelId(v.id)}>
                    <div class="edhist-item-top">
                      <span class="edhist-time">{relTime(v.createdAt)}</span>
                      <span class="spacer" />
                      <span class="muted mono" style="font-size:11px;">{v.charCount} ch</span>
                    </div>
                    <div class="edhist-item-prev mono">{firstLine(v.sqlText)}</div>
                  </div>
                )}
              </For>
            </Show>
          </div>

          <div class="edhist-detail">
            <Show when={selected()} fallback={<div class="muted" style="padding:14px;">Select a version.</div>}>
              <div class="edhist-detail-head">
                <span class="muted mono" style="font-size:12px;">{fmtTime(selected().createdAt)}</span>
                <span class="spacer" />
                <div class="edhist-toggle">
                  <button class="btn btn-sm" classList={{ 'btn-ghost': mode() !== 'diff' }} onClick={() => setMode('diff')}>
                    Diff vs current <span class="muted">(+{stats().added} −{stats().removed})</span>
                  </button>
                  <button class="btn btn-sm" classList={{ 'btn-ghost': mode() !== 'full' }} onClick={() => setMode('full')}>Full text</button>
                </div>
                <button class="btn btn-sm btn-primary" style="margin-left:8px;"
                        onClick={() => { props.onRestore?.(selected().sqlText); props.onClose?.(); }}>
                  ↺ Restore this version
                </button>
              </div>

              <Show when={mode() === 'diff'} fallback={<pre class="edhist-full mono">{selected().sqlText}</pre>}>
                <div class="edhist-diff mono">
                  <Show when={diff().length > 0 && (stats().added || stats().removed)}
                        fallback={<div class="muted" style="padding:10px;">This version is identical to the current editor content.</div>}>
                    <For each={diff()}>
                      {(op) => (
                        <div class={`edhist-line edhist-${op.type}`}>
                          <span class="edhist-sign">{op.type === 'add' ? '+' : op.type === 'del' ? '−' : ' '}</span>
                          <span class="edhist-text">{op.text || ' '}</span>
                        </div>
                      )}
                    </For>
                  </Show>
                </div>
              </Show>
            </Show>
          </div>
        </div>
      </Modal>
    </Show>
  );
}
